import { Steps } from "primereact/steps";
import { useState, useEffect, useCallback, useRef } from "react";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const stepDetails = [
  { title: "Overview" },
  { title: "Feature Types" },
  { title: "Data Scale" },
  { title: "Deployment" },
];

export default function Products() {
  const CONTENT_HEIGHT = 400;
  const [activeIndex, setActiveIndex] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(1);
  const [mdContent, setMdContent] = useState("");

  // 核心：探测当前步骤下有多少个 slide
  const checkAvailableSlides = useCallback(async (stepIdx: number) => {
    const validSlides: number[] = [];
    
    for (let i = 1; i <= 10; i++) {
      try {
        // 这里的关键是：获取响应对象并检查其内容类型
        const mdRes = await fetch(`/plan/step${stepIdx + 1}/s${i}.md`);
  
        const hasMd = mdRes.ok && mdRes.headers.get("content-type")?.includes("text/markdown");
        // 注意：有些服务器可能把 md 识别为 text/plain
        const isPlainText = mdRes.ok && mdRes.headers.get("content-type")?.includes("text/plain");
  
        // 如果返回的是 text/html，说明命中了前端路由回退，判定为不存在
        if ((hasMd || isPlainText)) {
          validSlides.push(i);
        } else {
          // 如果 s1 都不存在或者断开了，就停止探测
          break;
        }
      } catch (e) {
        break;
      }
    }
  }, []);

  // 当步骤切换时，探测 slide 数量并重置到第 1 个
  useEffect(() => {
    checkAvailableSlides(activeIndex);
    setCurrentSlide(1);
  }, [activeIndex, checkAvailableSlides]);

  // 当步骤或 Slide 变化时，加载文字内容
  useEffect(() => {
    const fetchMd = async () => {
      setMdContent(""); // 清空旧内容防止闪烁
      try {
        const response = await fetch(`/plan/step${activeIndex + 1}.md`);
        if (response.ok) {
          const text = await response.text();
          setMdContent(text);
        }
      } catch (err) {
        setMdContent("");
      }
    };
    fetchMd();
  }, [activeIndex, currentSlide]);

  const items = stepDetails.map((s, i) => ({
    label: s.title,
    command: () => setActiveIndex(i),
  }));

  //实现鼠标离开左右两边才恢复原样
  const [isExpanded, setIsExpanded] = useState(false);  
  // 💡 把 Ref 绑在内部的真实内容上，而不是外层动画壳子上
  const innerContentRef = useRef<HTMLDivElement>(null);
  const [innerRefHeight, setInnerRefHeight] = useState(0);
  // 实时监测内部 Markdown 纯文本到底有多高
  useEffect(() => {
    if (innerContentRef.current) {
      setInnerRefHeight(innerContentRef.current.scrollHeight);
    }
  }, [mdContent]);
  const handleMouseEnter = () => {
    setIsExpanded(true);
  };
  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    if (!container) return;
    const offsetX = e.nativeEvent.offsetX;
    const containerWidth = container.offsetWidth;
    // 纯物理方向判断
    const exitedLeft = offsetX <= 5;
    const exitedRight = offsetX >= (containerWidth - 5);
    if (exitedLeft || exitedRight) {
      setIsExpanded(false);
    }
  };
  // 判断内部文本是否真的超过了限高
  const hasMoreContent = innerRefHeight > CONTENT_HEIGHT;  
  // 计算卡片应该展示的高度：
  // 1. 如果没超过限高，那永远就是内部高度（innerRefHeight），不需要变高。
  // 2. 如果超过了限高：展开时变成真实的 innerRefHeight，收起时卡死在 CONTENT_HEIGHT。
  const currentHeight = hasMoreContent 
    ? (isExpanded ? innerRefHeight : CONTENT_HEIGHT) 
    : innerRefHeight;

  return (
    <>
        <section id="products" className="section" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
          <h2 style={{ color:"#3b5283 !important",fontSize: 36, textAlign: "center", marginBottom: "2.5rem", fontWeight: 600 }}>
            Plans
          </h2>

          <div style={{ maxWidth: 900, margin: "0 auto" }}>
            <Steps
              model={items}
              activeIndex={activeIndex}
              readOnly={false}
              onSelect={(e) => setActiveIndex(e.index)}
            />
            <div style={{ height: 4, background: "#eee", borderRadius: 999, marginTop: 12, overflow: "hidden" }}>
              <div
                style={{
                  height: "100%",
                  width: `${((activeIndex + 1) / stepDetails.length) * 100}%`,
                  background: "#111",
                  transition: "width 0.3s ease",
                }}
              />
            </div>
          </div>

          <div
            style={{
              maxWidth: 1000,
              margin: "0rem auto 0",
              padding: "0px",
              borderRadius: 16,
              border: "1px solid #eee",
              background: "#fff",
            }}
          >
            <div
            >
              {/* TEXT SIDE */}
              <div
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                style={{
                  height: currentHeight || CONTENT_HEIGHT, // 加上兜底                  
                  // 只有当内容确实超长、且处于展开状态时，才给大阴影；否则不加阴影或给极淡的阴影
                  boxShadow: (hasMoreContent && isExpanded) ? '0 12px 30px rgba(0,0,0,0.1)' : 'none',
                  // 丝滑动画
                  transition: "height 0.35s cubic-bezier(0.25, 1, 0.5, 1), box-shadow 0.3s ease",

                  overflow: "hidden",
                  position: "relative",
                  background: "#fff",
                  zIndex: 10,
                }}

              >
                <div ref={innerContentRef} style={{ width: '100%', overflow: 'hidden' }}>
                  <div className="markdown-body" style={{ padding: "10px",fontSize: 15, lineHeight: 1.6, color: "#444", paddingTop:"0px", marginTop:"0px" }}>
                    <ReactMarkdown 
                      remarkPlugins={[remarkGfm]}
                      components={{
                          // 拦截 table 标签
                          table: ({node, ...props}) => (
                            <div style={{ overflowX: 'auto' }}> {/* 移动端自适应滚动 */}
                              <table 
                                style={{ 
                                  width: '100%',     // 撑满父容器
                                  minWidth: '600px', // 确保在窄屏下不会挤成一团
                                  borderCollapse: 'collapse',
                                  margin: '1rem 0'
                                }} 
                                {...props} 
                              />
                            </div>
                          ),
                          // 可选：让表头文字更舒展
                          th: ({node, ...props}) => (
                            <th style={{ border: '1px solid #dfe2e5', padding: '12px 15px' }} {...props} />
                          ),
                          td: ({node, ...props}) => (
                            <td style={{ border: '1px solid #dfe2e5', padding: '12px 15px', textAlign: 'center' }} {...props} />
                          ),
                        }}
                    >
                      {mdContent}
                    </ReactMarkdown>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

    </>

  );
}
